import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  Inject,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';

export interface TocSection {
  id: string;
  label: string;
  active: boolean;
}

export interface DataItem {
  category: string;
  examples: string;
  purpose: string;
}

@Component({
    selector: 'app-privacy',
    imports: [CommonModule, RouterModule],
    templateUrl: './privacy.component.html',
    styleUrl: './privacy.component.css'
})
export class PrivacyComponent implements OnInit, AfterViewInit, OnDestroy {
  isBeta = false;
  lastUpdated = '5th March 2026';

  tocSections: TocSection[] = [
    { id: 'p1', label: '1. Introduction', active: false },
    { id: 'p2', label: '2. Who We Are', active: false },
    { id: 'p3', label: '3. Data We Collect', active: false },
    { id: 'p4', label: '4. How We Use Your Data', active: false },
    { id: 'p5', label: '5. Legal Basis for Processing', active: false },
    { id: 'p6', label: '6. Data Sharing & Disclosure', active: false },
    { id: 'p7', label: '7. Third-Party Services', active: false },
    { id: 'p8', label: '8. Google Cloud & Firebase', active: false },
    { id: 'p9', label: '9. Cookies & Tracking', active: false },
    { id: 'p10', label: '10. Data Retention', active: false },
    { id: 'p11', label: '11. Data Security', active: false },
    { id: 'p12', label: '12. International Transfers', active: false },
    { id: 'p13', label: "13. Children's Privacy", active: false },
    { id: 'p14', label: '14. Your Rights', active: false },
    { id: 'p15', label: '15. Beta Platform', active: false },
    { id: 'p16', label: '16. AI & Generated Content', active: false },
    { id: 'p17', label: '17. Changes to Policy', active: false },
    { id: 'p18', label: '18. Contact & Grievance', active: false },
  ];

  dataTable: DataItem[] = [
    {
      category: 'Identity Data',
      examples: 'Full name, username, profile picture',
      purpose: 'Account creation, personalisation, support',
    },
    {
      category: 'Contact Data',
      examples: 'Email address',
      purpose: 'Account notifications, billing, security alerts',
    },
    {
      category: 'Authentication Data',
      examples:
        'Hashed passwords, OAuth tokens (Google/GitHub via Firebase Auth)',
      purpose: 'Secure login and session management',
    },
    {
      category: 'Usage Data',
      examples:
        'Feature interactions, command patterns, session duration, page views',
      purpose: 'Platform improvement, analytics, AI model enhancement',
    },
    {
      category: 'Technical Data',
      examples: 'IP address, browser type, OS, device identifiers, time zone',
      purpose: 'Security monitoring, fraud prevention, diagnostics',
    },
    {
      category: 'Content Data',
      examples:
        'Code, prompts, project files uploaded or generated on the Platform',
      purpose:
        'Delivering the core service; not used for general AI training without consent',
    },
    {
      category: 'Payment Data',
      examples:
        'Billing name, last 4 digits, transaction IDs (processed by payment gateway)',
      purpose: 'Subscription billing — full card data never stored by us',
    },
    {
      category: 'Communication Data',
      examples: 'Support emails, feedback submissions, bug reports',
      purpose: 'Customer support, product improvement',
    },
  ];

  private intersectionObserver?: IntersectionObserver;
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private meta: Meta,
    private titleService: Title,
    private renderer: Renderer2,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.detectEnvironment();
    this.setMetaTags();
    this.applySecurityHeaders();
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.initScrollSpy();
    }
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  private detectEnvironment(): void {
    if (this.isBrowser) {
      const hostname = window.location.hostname;
      this.isBeta =
        hostname.startsWith('beta.') || hostname.includes('beta.angularize');
    }
  }

  get platformLabel(): string {
    return this.isBeta ? 'beta.angularize.dev' : 'angularize.dev';
  }

  private setMetaTags(): void {
    const title = 'Privacy Policy — Angularize.dev by Pythrust Technologies';
    this.titleService.setTitle(title);

    const description =
      'Angularize.dev Privacy Policy — how Pythrust Technologies OPC Private Limited collects, uses, and protects your personal data in compliance with Indian law, DPDPA 2023, and GDPR.';

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({
      property: 'og:url',
      content: 'https://angularize.dev/privacy-policy',
    });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });

    if (this.isBrowser) {
      this.ensureCanonical('https://angularize.dev/privacy-policy');
    }
  }

  private ensureCanonical(url: string): void {
    let link = document.querySelector(
      "link[rel='canonical']",
    ) as HTMLLinkElement;
    if (!link) {
      link = this.renderer.createElement('link') as HTMLLinkElement;
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.appendChild(document.head, link);
    }
    this.renderer.setAttribute(link, 'href', url);
  }

  private applySecurityHeaders(): void {
    if (!this.isBrowser) return;
    this.meta.updateTag({
      name: 'referrer',
      content: 'strict-origin-when-cross-origin',
    });
    this.meta.updateTag({
      'http-equiv': 'X-Content-Type-Options',
      content: 'nosniff',
    });
    this.meta.updateTag({ 'http-equiv': 'X-Frame-Options', content: 'DENY' });
    this.meta.updateTag({
      'http-equiv': 'Content-Security-Policy',
      content: [
        "default-src 'self'",
        "script-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
        "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://fonts.gstatic.com",
        "font-src 'self' https://fonts.gstatic.com",
        "img-src 'self' data: https:",
        "connect-src 'self'",
        "frame-ancestors 'none'",
        "base-uri 'self'",
        "form-action 'self'",
      ].join('; '),
    });
  }

  private initScrollSpy(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.tocSections.forEach(
              (s) => (s.active = s.id === entry.target.id),
            );
          }
        });
      },
      { rootMargin: '-15% 0px -80% 0px' },
    );

    this.tocSections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) this.intersectionObserver!.observe(el);
    });
  }

  scrollTo(id: string): void {
    if (!this.isBrowser) return;
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.isBrowser) window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
